import { z } from 'zod';

// Ported verbatim from Fireball.Ui/src/api/antiforgery/antiforgery.ts. The
// server issues an antiforgery token (header name + request token) that must be
// echoed back on mutating verbs. Cached in-module and refreshed once expired.
let antiforgery: AntiforgeryResponse | null = null;

const AntiforgeryResponseSchema = z.object({
	expiresAt: z.coerce.date(),
	headerName: z.string(),
	requestToken: z.string(),
});

export type AntiforgeryResponse = z.infer<typeof AntiforgeryResponseSchema>;

const getAntiforgeryToken = async (): Promise<AntiforgeryResponse> => {
	try {
		// Uses the (patched) global fetch so the request carries the bearer token
		// and is routed to VITE_API_URL + '/api/Antiforgery'. It is a GET, so the
		// patch does NOT attach an antiforgery header — no recursion.
		const response: Response = await fetch('/Antiforgery', {
			method: 'GET',
			credentials: 'include',
		});

		if (!response.ok) {
			throw new Error('Failed to fetch antiforgery token');
		}

		const data = await response.json();
		const validated = AntiforgeryResponseSchema.parse(data);

		antiforgery = validated;

		return validated;
	} catch (error) {
		console.error('Error fetching antiforgery token: ', error);
		throw error;
	}
};

export const ensureAntiforgeryToken =
	async (): Promise<AntiforgeryResponse> => {
		if (!antiforgery || Date.now() >= antiforgery.expiresAt.getTime()) {
			antiforgery = await getAntiforgeryToken();
		}
		return antiforgery;
	};
